"use client";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/ui/dialog";
import { getHotkeys } from "@/lib/hotkey-registry";
import { Text } from "@/ui/text";
import { TableCell, TableRow, TableBody, Table } from "@/ui/table";

const KEY_LABELS: Record<string, string> = {
  ctrl: "Ctrl",
  alt: "Alt",
  shift: "Shift",
  meta: "Cmd",
  slash: "/",
  comma: ",",
  enter: "Enter",
  escape: "Esc",
  backspace: "⌫",
  tab: "Tab",
  space: "Space",
};

function formatCombo(combo: string, isMac?: boolean): string {
  return combo
    .split("+")
    .map((k) => {
      if (k === "mod") return isMac ? "Cmd" : "Ctrl";
      return KEY_LABELS[k] ?? k.charAt(0).toUpperCase() + k.slice(1);
    })
    .join(" + ");
}

// Normalize ctrl↔meta so platform variants of the same chord count as one entry.
function normalizeCombo(combo: string): string {
  return combo.replace(/\bmeta\b/, "ctrl");
}

function formatKeys(keys: string): string {
  const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);
  const combos = keys.split(",").map((k) => k.trim());

  // Deduplicate ctrl/meta variants — keep the platform-appropriate one.
  const seen = new Set<string>();
  const deduped = combos.filter((combo) => {
    const norm = normalizeCombo(combo);
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });

  // On Mac prefer meta+, on others prefer ctrl+.
  const preferred = deduped.map((combo) => {
    const hasMeta = combos.some((c) => c === combo.replace(/\bctrl\b/, "meta"));
    const hasCtrl = combos.some((c) => c === combo.replace(/\bmeta\b/, "ctrl"));
    if (isMac && hasCtrl && hasMeta) return combo.replace(/\bctrl\b/, "meta");
    if (!isMac && hasCtrl && hasMeta) return combo.replace(/\bmeta\b/, "ctrl");
    return combo;
  });

  return preferred.map((combo) => formatCombo(combo, isMac)).join(" / ");
}

export function HotkeyHelpDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const hotkeys = open ? getHotkeys() : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogDescription>Available shortcuts in the current context.</DialogDescription>
        </DialogHeader>
        {hotkeys.length === 0 ? (
          <Text className="text-sm text-muted-foreground">No shortcuts registered.</Text>
        ) : (
          <Table className="w-full text-sm">
            <TableBody className="divide-y">
              {hotkeys.map(({ keys, description }) => (
                <TableRow key={keys} className="flex items-center justify-between py-2">
                  <TableCell className="text-muted-foreground">{description}</TableCell>
                  <TableCell>
                    <kbd className="bg-muted text-muted-foreground rounded px-2 py-0.5 font-mono text-xs">
                      {formatKeys(keys)}
                    </kbd>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
